import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { 
  TrendingUp, 
  TrendingDown,
  Camera, 
  Plus, 
  Scale,
  Target,
  Trophy,
  Flame,
  Share2,
  Calendar,
  Loader2,
  Image,
  ArrowRight,
  Award,
  Zap,
  CheckCircle,
  Download
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, AreaChart, Area } from 'recharts';

export default function MyTransformationsPage() {
  const { user, token } = useAuth();
  const [progress, setProgress] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLogDialog, setShowLogDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [logForm, setLogForm] = useState({
    weight_kg: '',
    body_fat_percentage: '',
    notes: '',
    photo_url: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProgress();
    fetchPhotos();
  }, [token]);

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API}/my-progress?days=90`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgress(response.data);
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API}/my-progress/photos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhotos(response.data);
    } catch (error) {
      console.error('Error fetching photos:', error);
    }
  };

  const logProgress = async () => {
    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      if (logForm.weight_kg) params.append('weight_kg', logForm.weight_kg);
      if (logForm.body_fat_percentage) params.append('body_fat_percentage', logForm.body_fat_percentage);
      if (logForm.notes) params.append('notes', logForm.notes);
      if (logForm.photo_url) params.append('photo_url', logForm.photo_url);

      await axios.post(
        `${API}/my-progress?${params}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Progress logged!');
      setShowLogDialog(false);
      setLogForm({ weight_kg: '', body_fat_percentage: '', notes: '', photo_url: '' });
      fetchProgress();
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to log progress');
    } finally {
      setSubmitting(false);
    }
  };

  const getShareableData = async () => {
    try {
      const response = await axios.get(`${API}/my-progress/share/card`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShareData(response.data);
      setShowShareDialog(true);
    } catch (error) {
      toast.error('Failed to generate share data');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const stats = progress?.stats || {};
  const milestones = progress?.milestones || [];
  const entries = progress?.progress_entries || [];

  // Prepare chart data
  const chartData = entries.map(e => ({
    date: e.date,
    weight: e.weight_kg,
    bodyFat: e.body_fat_percentage
  })).filter(d => d.weight || d.bodyFat);

  return (
    <div data-testid="transformations-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
            MY <span className="text-primary">TRANSFORMATION</span>
          </h1>
          <p className="text-gray-400 mt-1">Track your journey and celebrate your progress</p>
        </div>
        
        <div className="flex gap-3">
          <Button
            data-testid="share-progress-btn"
            onClick={getShareableData}
            variant="outline"
            className="border-border hover:border-primary"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share Progress
          </Button>
          
          <Dialog open={showLogDialog} onOpenChange={setShowLogDialog}>
            <DialogTrigger asChild>
              <Button data-testid="log-progress-btn" className="bg-primary hover:bg-red-700">
                <Plus className="w-4 h-4 mr-2" />
                Log Progress
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-white uppercase">
                  Log Your Progress
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Weight (kg)</Label>
                    <Input
                      type="number"
                      data-testid="progress-weight-input"
                      value={logForm.weight_kg}
                      onChange={(e) => setLogForm({...logForm, weight_kg: e.target.value})}
                      className="bg-secondary border-transparent"
                      placeholder={user?.weight_kg?.toString() || ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Body Fat %</Label>
                    <Input
                      type="number"
                      data-testid="progress-bodyfat-input"
                      value={logForm.body_fat_percentage}
                      onChange={(e) => setLogForm({...logForm, body_fat_percentage: e.target.value})}
                      className="bg-secondary border-transparent"
                      placeholder={user?.body_fat_percentage?.toString() || ''}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-300">Photo URL (optional)</Label>
                  <Input
                    type="url"
                    value={logForm.photo_url}
                    onChange={(e) => setLogForm({...logForm, photo_url: e.target.value})}
                    className="bg-secondary border-transparent"
                    placeholder="https://..."
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-300">Notes</Label>
                  <Textarea
                    value={logForm.notes}
                    onChange={(e) => setLogForm({...logForm, notes: e.target.value})}
                    className="bg-secondary border-transparent"
                    placeholder="How are you feeling? Any observations..."
                  />
                </div>
                
                <Button
                  data-testid="submit-progress-btn"
                  onClick={logProgress}
                  disabled={submitting}
                  className="w-full bg-primary hover:bg-red-700"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Progress'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Transformation Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-tactical">
          <div className="flex items-center justify-between mb-2">
            <Scale className="w-5 h-5 text-gray-400" />
            {stats.weight_change !== 0 && (
              stats.weight_change > 0 ? 
                <span className="text-red-400 text-sm flex items-center"><TrendingUp className="w-4 h-4 mr-1" />+{stats.weight_change?.toFixed(1)}kg</span> :
                <span className="text-green-500 text-sm flex items-center"><TrendingDown className="w-4 h-4 mr-1" />{stats.weight_change?.toFixed(1)}kg</span>
            )}
          </div>
          <div className="font-mono text-3xl font-bold text-white">{stats.current_weight || '-'}kg</div>
          <div className="text-sm text-gray-400 mt-1">
            Started: {stats.initial_weight || '-'}kg
          </div>
        </div>

        <div className="card-tactical">
          <div className="flex items-center justify-between mb-2">
            <Target className="w-5 h-5 text-gray-400" />
            {stats.body_fat_change !== 0 && (
              stats.body_fat_change > 0 ?
                <span className="text-red-400 text-sm flex items-center"><TrendingUp className="w-4 h-4 mr-1" />+{stats.body_fat_change?.toFixed(1)}%</span> :
                <span className="text-green-500 text-sm flex items-center"><TrendingDown className="w-4 h-4 mr-1" />{stats.body_fat_change?.toFixed(1)}%</span>
            )}
          </div>
          <div className="font-mono text-3xl font-bold text-white">{stats.current_body_fat || '-'}%</div>
          <div className="text-sm text-gray-400 mt-1">
            Started: {stats.initial_body_fat || '-'}%
          </div>
        </div>

        <div className="card-tactical">
          <div className="flex items-center gap-2 mb-2">
            <Flame className="w-5 h-5 text-primary" />
          </div>
          <div className="font-mono text-3xl font-bold text-white">{user?.current_streak || 0}</div>
          <div className="text-sm text-gray-400 mt-1">Day Streak</div>
        </div>

        <div className="card-tactical">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
          </div>
          <div className="font-mono text-3xl font-bold text-white">{user?.badges?.length || 0}</div>
          <div className="text-sm text-gray-400 mt-1">Badges Earned</div>
        </div>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="progress" className="w-full">
        <TabsList className="bg-secondary/50 border border-border">
          <TabsTrigger value="progress" className="data-[state=active]:bg-primary">
            <TrendingUp className="w-4 h-4 mr-2" />
            Progress Chart
          </TabsTrigger>
          <TabsTrigger value="photos" className="data-[state=active]:bg-primary">
            <Camera className="w-4 h-4 mr-2" />
            Photo Gallery
          </TabsTrigger>
          <TabsTrigger value="milestones" className="data-[state=active]:bg-primary">
            <Award className="w-4 h-4 mr-2" />
            Milestones
          </TabsTrigger>
        </TabsList>

        {/* Progress Chart Tab */}
        <TabsContent value="progress" className="mt-6">
          <div className="card-tactical">
            <h3 className="font-heading text-xl text-white uppercase mb-6">Weight & Body Fat Progress</h3>
            
            {chartData.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <XAxis 
                      dataKey="date" 
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fill: '#6B7280', fontSize: 12 }}
                    />
                    <YAxis 
                      yAxisId="weight"
                      orientation="left"
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fill: '#6B7280', fontSize: 12 }}
                      domain={['auto', 'auto']}
                    />
                    <YAxis 
                      yAxisId="bf"
                      orientation="right"
                      axisLine={false} 
                      tickLine={false} 
                      tick={{ fill: '#6B7280', fontSize: 12 }}
                      domain={[0, 40]}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#121212', 
                        border: '1px solid #27272A', 
                        borderRadius: '4px', 
                        color: '#fff' 
                      }}
                    />
                    <Line 
                      yAxisId="weight"
                      type="monotone" 
                      dataKey="weight" 
                      stroke="#E31B23" 
                      strokeWidth={3}
                      dot={{ fill: '#E31B23', strokeWidth: 2 }}
                      name="Weight (kg)"
                    />
                    <Line 
                      yAxisId="bf"
                      type="monotone" 
                      dataKey="bodyFat" 
                      stroke="#3B82F6" 
                      strokeWidth={3}
                      dot={{ fill: '#3B82F6', strokeWidth: 2 }}
                      name="Body Fat %"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <TrendingUp className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg mb-4">No progress data yet</p>
                  <Button onClick={() => setShowLogDialog(true)} className="bg-primary hover:bg-red-700">
                    Log Your First Entry
                  </Button>
                </div>
              </div>
            )}
            
            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-border">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-primary"></div>
                <span className="text-sm text-gray-400">Weight (kg)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-sm text-gray-400">Body Fat %</span>
              </div>
            </div>
          </div>
          
          {/* Recent Entries */}
          {entries.length > 0 && (
            <div className="card-tactical mt-4">
              <h4 className="font-heading text-lg text-white uppercase mb-4">Recent Entries</h4>
              <div className="space-y-2">
                {entries.slice(-5).reverse().map((entry, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm"
                  >
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-white">{entry.date}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      {entry.weight_kg && (
                        <span className="text-gray-300">
                          <Scale className="w-3 h-3 inline mr-1" />
                          {entry.weight_kg}kg
                        </span>
                      )}
                      {entry.body_fat_percentage && (
                        <span className="text-gray-300">
                          <Target className="w-3 h-3 inline mr-1" />
                          {entry.body_fat_percentage}%
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Photo Gallery Tab */}
        <TabsContent value="photos" className="mt-6">
          <div className="card-tactical">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl text-white uppercase">Photo Gallery</h3>
            </div>
            
            {photos.length > 0 ? (
              <>
                {/* Before/After Comparison */}
                {photos.length >= 2 && (
                  <div className="mb-8">
                    <h4 className="text-sm text-gray-400 uppercase mb-4">Transformation</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="relative">
                        <div className="absolute top-2 left-2 bg-black/70 px-2 py-1 rounded text-xs text-white">
                          BEFORE
                        </div>
                        <img 
                          src={photos[0].url} 
                          alt="Before" 
                          className="w-full aspect-[3/4] object-cover rounded-sm border border-border"
                        />
                        <div className="mt-2 text-center">
                          <div className="text-white font-mono">{photos[0].weight_kg || '-'}kg</div>
                          <div className="text-xs text-gray-400">{photos[0].date}</div>
                        </div>
                      </div>
                      <div className="relative">
                        <div className="absolute top-2 left-2 bg-primary px-2 py-1 rounded text-xs text-white">
                          AFTER
                        </div>
                        <img 
                          src={photos[photos.length - 1].url} 
                          alt="After" 
                          className="w-full aspect-[3/4] object-cover rounded-sm border border-primary"
                        />
                        <div className="mt-2 text-center">
                          <div className="text-white font-mono">{photos[photos.length - 1].weight_kg || '-'}kg</div>
                          <div className="text-xs text-gray-400">{photos[photos.length - 1].date}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* All Photos Grid */}
                <div>
                  <h4 className="text-sm text-gray-400 uppercase mb-4">All Photos ({photos.length})</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {photos.map((photo) => (
                      <div key={photo.id} className="group relative">
                        <img 
                          src={photo.url} 
                          alt={photo.date}
                          className="w-full aspect-square object-cover rounded-sm border border-border group-hover:border-primary transition-colors"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                          <div className="text-xs text-white">{photo.date}</div>
                          {photo.weight_kg && (
                            <div className="text-xs text-gray-300">{photo.weight_kg}kg</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="h-64 flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <Camera className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg mb-2">No photos yet</p>
                  <p className="text-sm mb-4">Add photos to track your visual transformation</p>
                  <Button onClick={() => setShowLogDialog(true)} className="bg-primary hover:bg-red-700">
                    <Camera className="w-4 h-4 mr-2" />
                    Add First Photo
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Milestones Tab */}
        <TabsContent value="milestones" className="mt-6">
          <div className="card-tactical">
            <h3 className="font-heading text-xl text-white uppercase mb-6">Your Milestones</h3>
            
            {milestones.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {milestones.map((milestone, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-sm border ${
                      milestone.achieved 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border bg-secondary/30 opacity-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        milestone.achieved ? 'bg-primary' : 'bg-secondary'
                      }`}>
                        {milestone.type === 'streak' && <Flame className="w-6 h-6 text-white" />}
                        {milestone.type === 'weight' && <Scale className="w-6 h-6 text-white" />}
                        {milestone.type === 'workout' && <Zap className="w-6 h-6 text-white" />}
                      </div>
                      <div>
                        <div className="text-white font-semibold">{milestone.name}</div>
                        <div className="text-sm text-gray-400">
                          {milestone.achieved ? (
                            <span className="text-green-500 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> Achieved
                            </span>
                          ) : (
                            'In Progress'
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <Trophy className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg mb-2">Keep going!</p>
                  <p className="text-sm">Complete workouts and track progress to earn milestones</p>
                </div>
              </div>
            )}
            
            {/* Upcoming Milestones */}
            <div className="mt-8 pt-6 border-t border-border">
              <h4 className="text-sm text-gray-400 uppercase mb-4">Next Milestones</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { name: '7-Day Streak', icon: Flame, current: user?.current_streak || 0, target: 7 },
                  { name: '30-Day Streak', icon: Flame, current: user?.current_streak || 0, target: 30 },
                  { name: '10 Workouts', icon: Zap, current: 0, target: 10 }
                ].filter(m => m.current < m.target).slice(0, 3).map((milestone, idx) => (
                  <div key={idx} className="p-4 bg-secondary/30 rounded-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <milestone.icon className="w-4 h-4 text-gray-400" />
                      <span className="text-white text-sm">{milestone.name}</span>
                    </div>
                    <Progress 
                      value={(milestone.current / milestone.target) * 100} 
                      className="h-2"
                    />
                    <div className="text-xs text-gray-400 mt-1">
                      {milestone.current} / {milestone.target}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white uppercase">
              Share Your Transformation
            </DialogTitle>
          </DialogHeader>
          
          {shareData && (
            <div className="py-4">
              {/* Preview Card */}
              <div className="bg-gradient-to-br from-primary/20 to-secondary p-6 rounded-sm border border-primary mb-4">
                <div className="text-center">
                  <div className="text-sm text-gray-400 uppercase mb-2">Bodies By Akeem</div>
                  <div className="font-heading text-2xl text-white uppercase mb-4">
                    {shareData.user?.first_name}'s Transformation
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <div className="font-mono text-2xl text-primary font-bold">
                        {shareData.transformation?.weight_lost?.toFixed(1) || 0}kg
                      </div>
                      <div className="text-xs text-gray-400">Lost</div>
                    </div>
                    <div>
                      <div className="font-mono text-2xl text-white font-bold">
                        {shareData.transformation?.current_streak || 0}
                      </div>
                      <div className="text-xs text-gray-400">Day Streak</div>
                    </div>
                    <div>
                      <div className="font-mono text-2xl text-yellow-500 font-bold">
                        {shareData.transformation?.badges_earned || 0}
                      </div>
                      <div className="text-xs text-gray-400">Badges</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                  <Share2 className="w-4 h-4 mr-2" />
                  Share to Social Media
                </Button>
                <Button variant="outline" className="w-full border-border hover:border-primary">
                  <Download className="w-4 h-4 mr-2" />
                  Download Image
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
